import { useEffect, useRef, useState } from "react";
import { listCompanyNamesAdmin } from "../../utils/api/endpoints/companies";

type CompanyOption = {
    company_id: number;
    company_name: string;
};

const CompanyFilterDropdown = ({
    selectedIds,
    onChange,
}: {
    selectedIds: number[];
    onChange: (ids: number[]) => void;
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [companies, setCompanies] = useState<CompanyOption[]>([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Fetch companies on mount
    useEffect(() => {
        setLoading(true);
        listCompanyNamesAdmin()
            .then((data) => setCompanies(data || []))
            .catch(() => setCompanies([]))
            .finally(() => setLoading(false));
    }, []);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredCompanies = companies.filter((c) =>
        c.company_name.toLowerCase().includes(search.toLowerCase())
    );

    const toggleCompany = (id: number) => {
        if (selectedIds.includes(id)) {
            onChange(selectedIds.filter((i) => i !== id));
        } else {
            onChange([...selectedIds, id]);
        }
    };

    const clearAll = () => {
        onChange([]);
        setSearch("");
    };

    const buttonLabel =
        selectedIds.length === 0
            ? "All Companies"
            : `${selectedIds.length} Compan${selectedIds.length === 1 ? "y" : "ies"}`;

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Trigger Button */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 rounded-lg border px-4 py-3 text-sm transition"
                style={{
                    borderColor: selectedIds.length > 0 ? "#2563EB" : "#D1D5DB",
                    color: selectedIds.length > 0 ? "#2563EB" : "#0B1739",
                    backgroundColor: selectedIds.length > 0 ? "#EFF6FF" : "#FFFFFF",
                    width: "100%",
                }}
            >
                {/* Building icon */}
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                </svg>
                <span className="flex-grow text-left">{buttonLabel}</span>
                <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="currentColor"
                    className={
                        "transition-transform " + (isOpen ? "rotate-180" : "rotate-0")
                    }
                >
                    <path d="M6 9L2 5h8z" />
                </svg>
            </button>

            {/* Dropdown Panel */}
            {isOpen && (
                <div
                    className="absolute left-0 top-full z-50 mt-1 w-full min-w-72 rounded-lg bg-white shadow-lg"
                    style={{ border: "1px solid #E5E7EB" }}
                >
                    {/* Search inside dropdown */}
                    <div className="p-3" style={{ borderBottom: "1px solid #F3F4F6" }}>
                        <input
                            type="text"
                            placeholder="Search companies..."
                            className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2"
                            style={{ borderColor: "#D1D5DB" }}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            autoFocus
                        />
                    </div>

                    {/* Company List */}
                    <div
                        className="max-h-60 overflow-y-auto"
                        style={{ scrollbarWidth: "thin" }}
                    >
                        {loading ? (
                            <div className="flex items-center justify-center py-6 text-sm text-gray-400">
                                Loading...
                            </div>
                        ) : filteredCompanies.length === 0 ? (
                            <div className="flex items-center justify-center py-6 text-sm text-gray-400">
                                No companies found
                            </div>
                        ) : (
                            filteredCompanies.map((company) => {
                                const isSelected = selectedIds.includes(company.company_id);
                                return (
                                    <label
                                        key={company.company_id}
                                        className="flex cursor-pointer items-center gap-3 px-4 py-2.5 transition-colors hover:bg-gray-50"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleCompany(company.company_id)}
                                            className="h-4 w-4 rounded accent-blue-600"
                                        />
                                        <div className="flex flex-col min-w-0">
                                            <span
                                                className="text-sm font-medium truncate"
                                                style={{ color: "#1F2937" }}
                                            >
                                                {company.company_name}
                                            </span>
                                            <span
                                                className="text-xs"
                                                style={{ color: "#9CA3AF" }}
                                            >
                                                ID: {company.company_id}
                                            </span>
                                        </div>
                                    </label>
                                );
                            })
                        )}
                    </div>

                    {/* Footer */}
                    {selectedIds.length > 0 && (
                        <div
                            className="flex items-center justify-between px-4 py-2.5"
                            style={{ borderTop: "1px solid #F3F4F6" }}
                        >
                            <span className="text-xs" style={{ color: "#6B7280" }}>
                                {selectedIds.length} selected
                            </span>
                            <button
                                onClick={clearAll}
                                className="text-xs font-medium transition hover:underline"
                                style={{ color: "#2563EB" }}
                            >
                                Clear all
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CompanyFilterDropdown;
